<template>
    <vx-modal :padding="false" v-model:visible="visible" size="md">
        <template v-slot:header>
            <div class="pt-6 px-6 pb-4">{{ props.categoryId !== '' ? 'Update' : 'New' }} Category</div>
        </template>

        <div class="p-6">
            <div class="mb-3">
                <vx-label>Color</vx-label>
                <div class="flex gap-2">
                    <div
                        v-for="[key, color] of Object.entries(CATEGORY_COLORS)"
                        :key="key"
                        tabindex="0"
                        :class="[
                            'cursor-pointer hover:scale-110 active:scale-90 rounded size-10 transition ring-2 ring-offset-2 outline-none',
                            color.bg,
                            formData.color === key
                                ? `${color.ring} focus-visible:ring-blue-500`
                                : 'ring-transparent focus-visible:ring-blue-500',
                        ]"
                        @click="formData.color = key as IntelligentCategoryColor"
                    ></div>
                </div>
            </div>
            <div class="mb-3">
                <vx-label>Name</vx-label>
                <vx-input
                    v-model="formData.displayName"
                    size="lg"
                    :error="displayFirstError(validation.displayName)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Description (optional)</vx-label>
                <vx-input
                    v-model="formData.description"
                    size="lg"
                    :rows="3"
                    :error="displayFirstError(validation.description)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Classification Hints</vx-label>
                <vx-input
                    v-model="formData.classificationHints"
                    size="lg"
                    :rows="4"
                    placeholder="Describe what makes a conversation belong to this category..."
                    :error="displayFirstError(validation.classificationHints)"
                >
                    <template v-slot:hint> These hints guide the AI in classifying conversations. </template>
                </vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Response Hint</vx-label>
                <vx-input
                    v-model="formData.responseHint"
                    size="lg"
                    :rows="3"
                    placeholder="Guidance for suggested responses in this category."
                    :error="displayFirstError(validation.responseHint)"
                ></vx-input>
            </div>
            <div>
                <toggle-with-content
                    v-model="formData.active"
                    title="Active"
                    subtitle="Include in classification"
                ></toggle-with-content>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" @click="visible = false"> Cancel </vx-button>
                <vx-button
                    class="grow"
                    color="primary"
                    size="lg"
                    type="button"
                    :loading="processing"
                    @click.prevent="submit()"
                >
                    {{ props.categoryId !== '' ? 'Update' : 'Create' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxInput, VxLabel, VxModal } from '@voxie/frontend-components';
import { required, maxLength } from '@vuelidate/validators';
import { ref, watch } from 'vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import ToggleWithContent from '~/components/general/ToggleWithContent.vue';
import { displayFirstError } from '~/utils/validation';
import { CATEGORY_COLORS, createEmptyCategory, useIntelligentInbox } from './useIntelligentInbox';
import type { IntelligentCategory, IntelligentCategoryColor } from './useIntelligentInbox';

const props = defineProps<{ categoryId: string }>();
const visible = defineModel<boolean>('visible', { required: true });

const { categories, addCategory, updateCategory } = useIntelligentInbox();

const formData = ref<IntelligentCategory>(createEmptyCategory());

watch(visible, (isVisible) => {
    if (isVisible) {
        if (props.categoryId !== '') {
            const existing = categories.value.find((c) => c.id === props.categoryId);
            if (existing) {
                formData.value = { ...existing };
            }
        } else {
            formData.value = createEmptyCategory();
        }
    } else {
        validation.value.$reset();
    }
});

const { submit, processing, validation } = useFormSubmission({
    data: formData,
    validation: {
        displayName: { required, maxLength: maxLength(120) },
        description: { maxLength: maxLength(500) },
        classificationHints: { maxLength: maxLength(2000) },
        responseHint: { maxLength: maxLength(1000) },
    },
    submit: async () => {},
    onSuccess: () => {
        if (props.categoryId !== '') {
            updateCategory(props.categoryId, { ...formData.value });
        } else {
            addCategory({ ...formData.value });
        }
        visible.value = false;
    },
    preserveScroll: true,
});
</script>
